import React, { useState, useEffect, useRef } from 'react'
import JSZip from 'jszip';
import '../styles/Batch.css'
import useBatchStatusApi from '../Hooks/useBatchStatusApi.js';
import useBatchDataAccessApi from '../Hooks/useDataAccessApi.js';
import useAuthToken from '../Hooks/useAuthToken';
import BatchInfo from '../Components/Combined/BatchInfo';
import * as uuid from 'uuid';
import Loader from '../Components/Simple/Loader';
import ApiError from '../Components/Combined/ApiError';
import { Button, ErrorMessage } from '../Components/Simple';
import FilePickerButton from '../Components/Simple/FilePickerButton';
import useLocalBatchData from '../Hooks/useLocalBatchData';
import { enableDeletion/*, enableBatchRowLimit, enableMoreInfoButton */} from '../Utils/features';
import useSubmissionApi from '../Hooks/useSubmissionApi.js';
import { Collapse } from 'bootstrap';
//import DownloadLink from '../Components/Simple/MrpDownloadLink.js';

const outputFieldConfig = {
    SequenceNumber: true,
    ClaimID: true,
    LineNum: true,
    MemberID: true,
    ProviderGroup: true,
    ProviderCounty: true,
    MedicareID: true,
    EncounterID: true,
    MillimanBilled: true,
    MillimanAllowed: true,
    MillimanCurrentAllowed: true,
    ServiceType: true,
    RVU_DRG_HCPCS: true,
    RVU_DRG_HCPCS_TYPE: true,
    RVUAdjustedUnits: true,
    RVUFeeSchedule: true,
    RVUWorkRVUs: true,
    RVUPracticeRVUs: true,
    RVUMalpracticeRVUs: true,
    RVUFinalRVUs: true,
    RVUImputeBasis: true,
    RVUFinalRVUStep: true,
    RVUAdjudicated: true,
    RVUInclude: true,
    MedicareAdjustedUnits: true,
    MedicareAPC_DRG_HCPCS: true,
    MedicareAPC_DRG_HCPCS_TYPE: true,
    MedicareStatusLookup: true,
    MedicareFeeSchedule: true,
    MedicareAllowedBaseNTNWD: true,
    MedicareAllowedBase: true,
    MedicareOutlier: true,
    MedicareDSH_UCP: true,
    MedicareCapIME: true,
    MedicareOpIME: true,
    MedicareNewTech: true,
    MedicarePassThru_DGME: true,
    MedicarePassThru_Organ: true,
    MedicarePassThru_AlloStemCell: true,
    MedicarePassThru_Other: true,
    MedicareOrganAcqCharge: true,
    MedicareAllowed: true,
    MedicareAdjudicationErrors: true,
    FinancialValidationErrors: true,
    MedicareAdjudicated: true,
    MedicareInclude: true
};

export default function BatchSubmit() {
    const [fileSize, setFileSize] = useState(0);
    const [selectedFile, setSelectedFile] = useState(null);
    const [batchList, setBatchList] = useState([]);
    const [open, setOpen] = useState(false);
    const [uploadComplete, setUploadComplete] = useState(false);
    const accessToken = useAuthToken();
    const [submissionSuccess, setSubmissionSuccess] = useState(false);


    const { loading, error, result } = useBatchStatusApi();
    const { upload, uploading, progress } = useBatchDataAccessApi();
    const localBatchData = useLocalBatchData();
    const [fileError, setFileError] = useState('');
    const { submissionLoading, submissionError, submissionExecute } = useSubmissionApi();
    const loaded = useRef(false);
    //const [activeSection, setActiveSection] = useState(null);
    //const [activeButton, setActiveButton] = useState('Claims');
    
    // Set Default button to Claims
    /*useEffect(() => {
        setActiveSection('Claims');
    }, []); */

    useEffect(() => {
        if (!accessToken.objectId) {
            return;
        }

        setBatchList(bl => {
            const localCopies = localBatchData.get(accessToken.objectId);
            if (bl.length === 0) {
                return localCopies;
            }

            const newList = [];
            
            for(const batch of bl) {
                const matchingBatch = localCopies.find(r => r.batchId === batch.batchId);
                if (matchingBatch && matchingBatch.batchCanceled) {
                    newList.push(matchingBatch);
                } else {
                    newList.push(batch);
                }
            }

            for (const localCopy of localCopies) {
                if (!bl.some(l => l.batchId === localCopy.batchId)) {
                    newList.push(newBatch);
                }
            }

            return newList;
        });
    }, [accessToken.objectId]);

    useEffect(() => {
        if (!result) {
            return;
        }

        setBatchList(bl => {
            const newList = [];
            for (const batch of bl) {
                const matchingBatch = result.find(r => r.batchId === batch.batchId);
                if (!batch.batchCanceled && matchingBatch) {
                    newList.push(matchingBatch);
                } else if (batch.local) {
                    // only maintain the records that are not local
                    newList.push(batch);
                }
            }

            for (const newBatch of result) {
                if (!bl.some(l => l.batchId === newBatch.batchId)) {
                    newList.push(newBatch);
                }
            }

            return newList;
        });

        if (!loaded.current) {
            loaded.current = true;
        }
    }, [result]);

    useEffect(() => {
        if (!loaded.current) {
            return;
        }
    
        const filteredList = batchList.filter((bl) => bl.local /*&& bl.section === activeSection*/);
        localBatchData.save(filteredList, accessToken.objectId);
    }, [batchList/*, activeSection*/]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setUploadComplete(false);
        setSubmissionSuccess(false);
        setFileError('');
    
        try {
            const uploadFilePath = await submissionExecute();
            if (uploadFilePath) {
                setSubmissionSuccess(true);
                await uploadFile(uploadFilePath);
            } else {
                setFileError('Submission did not return a valid file path.');
            }
        } catch (error) {
            console.error("Submission error:", error);
            setFileError('An error occurred during submission.');
        }

        //const newSection = activeSection === 'Hospital' ? 'Hospital' : 'Claims';
        /*setBatchList(bl => {
            return [
                ...bl,
                {
                    local: true,
                    batchId: uuid.v4(),
                    uploads: [],
                    section: newSection,
                }
            ]
        })*/
        setSelectedFile(null);
    };

    const handleFileChange = (event) => {
        setFileError('');
        setUploadComplete(false);
        setSubmissionSuccess(false);

        const file = event.target.files[0];

        const fileType = file.name.split('.').pop();
        if (fileType?.toLowerCase() !== 'zip') {            
            setFileError('File not a zip archive, please create a zip archive and retry.');
            return;
        }

        if (file.size >= 5368709120) { // file is 5GB or larger
            setFileError('File is larger than 5GB, please split into smaller parts and upload individually');
            return;
        }

        setSelectedFile(file);
    };

    const handleOnCancelled = (batchId) => {
        setBatchList(bl => {
            const matchingBatch = bl.find(b => b.batchId === batchId);
            if (!matchingBatch) {
                return bl;
            } 

            matchingBatch.local = true;
            matchingBatch.batchCanceled = true;

            return [
                ...bl.map(b => {
                    return {
                        ...b
                    }
                })
            ];
        });
    };

    const handleOnDeleted = (batchId) => {
        setBatchList(bl => {
            const matchingBatch = bl.find(b => b.batchId === batchId);
            if (!matchingBatch) {
                return bl;
            } 

            matchingBatch.local = true;
            matchingBatch.batchDeleted = true;
    
            return [
                ...bl.map(b => {
                    return {
                        ...b
                    }
                })
            ];
        });
        
    };

    const uploadFile = async (uploadFilePath) => {
        if (!selectedFile) {
            console.error("No file selected for upload.");
            return;
        }
        
        const successfulUpload = await upload(selectedFile, uploadFilePath);
        if (successfulUpload) {
            setUploadComplete(true);
        } else {
            console.error("Upload error:", error);
            setFileError('An error occurred during file upload.');
        }
        
    };

    return (
        <div className="row py-2">
            <div className="col">
                <div className="row justify-content-between my-2">
                    <div className="col-auto">
                        <h3>Indiana Data Submission Portal</h3>
                    </div>
                </div>
                <div>
                        <p class="d-inline-flex gap-1">
                            <button class="btn btn-primary" type="button" data-bs-toggle="collapse" data-bs-target="#collapseExample" aria-expanded="false" aria-controls="collapseExample">
                                More Info
                            </button>
                        </p>
                        <div class="collapse show" id="collapseExample">
                            <div class="card card-body">
                                <h4>
                                    About 
                                </h4>
                                <p>
                                    This portal will allow you to securely transfer the data needed by the Indiana Department of Insurance to meet the requirements of HEA 1004.    
                                </p> 
                                <h4>
                                    How to upload
                                </h4>
                                <p>
                                    1) Prepare the hospital claims data and Appendix C Control Total template as described in the Data Submission Guide, available on the <a href="https://milliman.sharepoint.com/teams/idoihospitalbenchmarking/" target="_blank">IDOI Hospital Benchmarking SharePoint site</a>.
                                    <br></br>
                                    2) Zip the data file. The control total template may be included in the same zip file as the detailed data (recommended).
                                    <br></br>
                                    3) The maximum size for a single zip file upload is 5GB. Please package the data into multiple zip files so that each file is under the limit.
                                    <br></br>
                                    4) Click on the "Upload .zip file" button to select the file for uploading.
                                    <br></br>
                                    5) Once the file is selected, click on the "Upload" button to upload the claims file.
                                    
                                </p>
                            </div>
                        </div>
                </div>
                
                <hr />
                <ApiError error={error} />
                <div className="row justify-content-between align-items-center mt-3">
                    <div className="col">
                        <ErrorMessage errors={[fileError]} />
                        <form onSubmit={handleSubmit}>
                            <div className="row align-items-center">
                                <div className="col-auto">
                                    <FilePickerButton id="batch-upload"
                                        text="Upload .zip file"
                                        className="btn-dark"
                                        disabled={uploading || submissionLoading}
                                        onChange={handleFileChange} />
                                </div>
                                <div className="col-4">
                                    {selectedFile?.name || 'No file selected'}
                                </div>
                                <div className="col-auto">
                                    <Button text="Upload"
                                        id="upload_button"
                                        type="submit"
                                        className="btn-primary"
                                        disabled={!selectedFile || uploading || submissionLoading}
                                        loading={uploading || submissionLoading} />
                                </div>
                                <div className="col-auto">
                                    <Button text="Clear"
                                        id="clear-upload-button"
                                        className="btn-secondary"
                                        disabled={!selectedFile || uploading || submissionLoading}
                                        onClick={() => setSelectedFile(null)} />
                                </div>
                                {uploading && <div className="col-auto">
                                    {progress.toFixed(2)}%
                                </div>}
                            </div>


                            <p id="upload_status"></p>
                            <p id="elig_upload"></p>
                        </form>
                    </div>
                    <div className="col-auto">
                        {loading && <Loader />}
                    </div>
                </div>
                <hr />
                {
                /*
                <div className="row align-items-center">
                    <div className='col-auto'>
                        <button
                            onClick={() => {
                                setActiveSection('Claims');
                                setActiveButton('Claims');
                            }}
                            className={activeButton === 'Claims' ? 'active-button' : ''}
                        >
                            Claims
                        </button>
                    </div>
                    <div className='col-auto'>
                        <button
                            onClick={() => {
                                setActiveSection('Hospital');
                                setActiveButton('Hospital');
                            }}
                            className={activeButton === 'Hospital' ? 'active-button' : ''}
                        >
                            Hospital
                        </button>
                    </div>
                </div>
                <hr />*/
                }
                {/*
                {activeSection === 'Claims' && (
                    <>
                        <p>Active Batch Processing</p>
                        <div className="batch-list">
                            {batchList
                                .filter((b) => b.local && b.section === 'Claims')
                                .map((b, index) => (
                                    <BatchInfo
                                        key={index}
                                        batch={b}
                                        onCancel={handleOnCancelled}
                                        onDelete={enableBatchDeletion ? handleOnDeleted : undefined}
                                    />
                                ))}
                        </div>
                    </>
                )} 

                {activeSection === 'Hospital' && (
                    <>
                        <p>Hospital Information</p>
                        <div className="batch-list">
                            {batchList
                                .filter((b) => b.local && b.section === 'Hospital')
                                .map((b, index) => (
                                    <BatchInfo
                                        key={index}
                                        batch={b}
                                        onCancel={handleOnCancelled}
                                        onDelete={enableBatchDeletion ? handleOnDeleted : undefined}
                                    />
                                ))}
                        </div>
                    </>
                )}
                */
                }
                {/*Previously Uploaded Files*/}
                <div className="batch-list">
                    {uploadComplete && <div className="alert alert-success" role="alert">Upload completed successfully!</div>}
                    {batchList.map((b, index) => {
                        return (<BatchInfo
                            key={index}
                            batch={b}
                            onCancel={handleOnCancelled}
                            onDelete={enableDeletion ? handleOnDeleted : undefined}
                        />)
                    })}

                </div>
            </div>
        </div>
    )
};